import {PayloadAction, createSlice} from '@reduxjs/toolkit';

import {ExperimentKey} from '~shared/constants';
import type {WFClientAssets, WFSettings, YfmSettings} from '~shared/models';

import {inject} from '../store';

import {updateUserSettings} from './user-settings';

export interface GlobalState {
    staffHost?: string;
    oauthUrl?: string;
    trackerHost: string;
    avatarsHost: string;
    collabUrl: string;
    collabChunkSize: string;
    wfClient?: WFClientAssets;
    wfSettings: WFSettings;
    yfmSettings: YfmSettings;
    isTouch?: boolean;
    appInstallation: 'intranet' | 'business';
    experiments: Partial<Record<ExperimentKey, boolean>>;
    forceDesktop?: boolean;
}

const initialState: GlobalState = {
    trackerHost: '',
    avatarsHost: '',
    wfSettings: {},
    yfmSettings: {},
    appInstallation: 'intranet',
    experiments: {},
    collabUrl: '',
    collabChunkSize: '',
};

export const {reducer, actions} = createSlice({
    name: 'global',
    initialState,
    reducers: {
        setYfmSettingsPageSlug: (state, {payload: {slug}}: PayloadAction<{slug: string}>) => {
            state.yfmSettings.wikiPath = slug;
            if (state.yfmSettings.pluginOptions) state.yfmSettings.pluginOptions.wikiPath = slug;
        },
    },
    extraReducers: (builder) =>
        builder.addCase(updateUserSettings.pending, (state, {meta}) => {
            const {theme} = meta.arg;

            if (theme) {
                state.yfmSettings.pluginOptions = {
                    ...state.yfmSettings.pluginOptions,
                    theme,
                };
            }
        }),
});

const KEY = 'global';
inject(KEY, reducer);
declare global {
    interface WikiState {
        [KEY]: GlobalState;
    }
}

export const selectGlobalState = (state: WikiState) => state.global;
export const selectExperiments = (state: WikiState) => state.global.experiments;
export const selectExperimentByKey =
    <K extends keyof GlobalState['experiments']>(key: K) =>
    (state: WikiState) =>
        state.global.experiments[key];

export const selectCollabExperiment = (state: WikiState) => {
    const exps = selectExperiments(state);
    return exps[ExperimentKey.Collab];
};

export const selectInformerExperiment = (state: WikiState) => {
    const exps = selectExperiments(state);
    return exps[ExperimentKey.Informer];
};

export const selectInfobarExperiment = (state: WikiState) => {
    const exps = selectExperiments(state);
    return exps[ExperimentKey.Infobar];
};

export const getAppInstallationSelector = (state: WikiState) => state.global.appInstallation;
